import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, DialogActions, DialogContent, Popover, TextField } from "@mui/material";
import { UserActions } from "./user_actions_enum";
import useBackend, { useLogin, useSetLogin } from "../../hooks/use_backend";
import { refresh } from "../login/login_utils";
import { createUser, updateMyUser, updateUser, validateUserName } from "../../scripts/api_v2/user_client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Snackbar from "../../scripts/snackbar";
import { MaterialReactTable, MRT_TablePagination } from "material-react-table";
import { themeOptions } from "../../app";
import { CustomRoleSelectField, CustomTextField, PasswordField } from "../../components/form/form_fields";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";

const SearchAndSelectTable = ({
  searchPlaceholder,
  searchTerm,
  handleSearchChange,
  columns,
  filteredData,
  isLoading,
  onRowSelectionChange,
  colKey,
  selectedEntry,
}) => (
  <>
    <TextField
      placeholder={searchPlaceholder}
      variant="outlined"
      fullWidth
      margin="normal"
      value={searchTerm}
      onChange={handleSearchChange}
    />
    <MaterialReactTable
      columns={columns}
      data={filteredData}
      enableTopToolbar={false}
      enableTableHead={false}
      enableGlobalFilter={false}
      enableRowSelection={false} // Disables the default ugly checkboxes for row selection...
      enableMultiRowSelection={false}
      initialState={{
        density: "compact",
        pagination: {
          pageIndex: 0,
          pageSize: 5, // Set the initial rows per page to 5
        },
      }}
      state={{
        isLoading,
      }}
      renderBottomToolbar={({ table }) => (
        <Box component="nav" aria-label="Company pagination" sx={{ display: "flex", justifyContent: "flex-end" }}>
          <MRT_TablePagination table={table} />
        </Box>
      )}
      muiTableBodyRowProps={({ row }) => {
        return {
          onClick: () => {
            onRowSelectionChange(row.original[colKey]);
          },
          sx: {
            backgroundColor: selectedEntry === row.original[colKey] ? themeOptions.palette.primary.main : "inherit",
            cursor: "pointer",
          },
        };
      }}
      muiTableBodyCellProps={{
        sx: { textAlign: "center", padding: "8px" },
      }}
    />
  </>
);

const UserPopover = ({
  open,
  anchorEl,
  userRow,
  actionType,
  onClose,
  partnerCompaniesPickList,
  companiesError,
  userRolesPickList,
  rolesError,
}) => {
  const setLogin = useSetLogin();
  const backend = useBackend();
  const login = useLogin();
  const queryClient = useQueryClient();
  // general form state
  const [isValid, setIsValid] = useState(false);
  const [editUserData, setEditUserData] = useState({});
  const [validityChecks, setValidityChecks] = useState({});
  // password fields state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // validation and access variables
  const PASSWORD_MIN_LENGTH = 8;
  const NAME_MIN_LENGTH = 3;
  const NAME_MAX_LENGTH = 200;
  const companySearchAndSelectKey = "company_name";
  //
  const defaultCompany = React.useMemo(
    () => getDefaultCompany(userRow.company_name, partnerCompaniesPickList),
    [userRow, partnerCompaniesPickList],
  );
  const [searchAndSelectSearchTerm, setSearchAndSelectSearchTerm] = useState(defaultCompany?.company_name);
  const [selectedSearchAndSelectEntry, setSelectedSearchAndSelectEntry] = useState(defaultCompany?.company_name ?? "");
  const [filteredSearchAndSelectData, setFilteredSearchAndSelectData] = useState(partnerCompaniesPickList ?? []);

  const userRolePickListOptions = userRolesPickList
    ? userRolesPickList.map((role) => role.role_name).sort((a, b) => a.localeCompare(b))
    : [];

  const getDefaultRoles = () => {
    const roleName =
      userRolePickListOptions?.find((role) => role.toLowerCase().includes("organisation_installer")) ||
      userRolePickListOptions?.[0] ||
      "";
    return [
      {
        role_name: roleName,
      },
    ];
  };

  function getDefaultCompany(userRowCompany, partnerCompaniesData) {
    if (userRowCompany) {
      return partnerCompaniesData?.find((company) => company.company_name === userRowCompany);
    } else {
      return partnerCompaniesData?.[0] ?? null;
    }
  }

  // loop over the entries in the users_action_enum, check initial validity
  const generateInitialValidity = (requiredValidationList, userRow) => {
    requiredValidationList = requiredValidationList ?? [];
    return requiredValidationList.reduce((acc, key) => {
      acc[key] = validateFieldByKey(key, userRow[key], userRow);
      return acc;
    }, {});
  };

  // filter search and select table based on search and select changes
  useEffect(() => {
    if (partnerCompaniesPickList) {
      setFilteredSearchAndSelectData(
        doFilterData(partnerCompaniesPickList, companySearchAndSelectKey, searchAndSelectSearchTerm),
      );
    }
  }, [partnerCompaniesPickList, searchAndSelectSearchTerm]);

  // generic filter function
  function doFilterData(data, searchKey, searchTerm) {
    return searchTerm
      ? data.filter((item) => {
          return item[searchKey].toLowerCase().includes(searchTerm?.toLowerCase());
        })
      : data;
  }

  //track changes when new row/action is clicked and pop-up reopens
  useEffect(() => {
    let userData = {
      name: userRow.name ?? "",
      username: userRow.username ?? "",
      company_name: defaultCompany?.[companySearchAndSelectKey] ?? userRow.company_name,
      roles: userRow.roles && userRow.roles.length > 0 ? userRow.roles : getDefaultRoles(),
      currentPassword: "",
      password: "",
      confirmPassword: "",
    };
    setEditUserData(userData);
    setValidityChecks(generateInitialValidity(actionType.requiredValidationList, userData));
  }, [userRow, actionType, defaultCompany]);

  // hook to update search and select term state
  const handleSearchChange = (event) => {
    setSearchAndSelectSearchTerm(event.target.value);
  };

  // handle changes in selected row for the search and select table
  function onTableRowSelectionChange(entry_key) {
    setSelectedSearchAndSelectEntry(entry_key);
    setSearchAndSelectSearchTerm(entry_key);
    handleFieldValueChanges(companySearchAndSelectKey, entry_key);
  }

  const handleFieldValueEvent = async (e) => {
    // unpack the event
    const { name, value } = e.target;
    await handleFieldValueChanges(name, value);
  };

  const handleFieldValueChanges = async (name, value) => {
    // Update the user data state
    setEditUserData((prevState) => ({ ...prevState, [name]: value }));
    // Update validity checks state
    setValidityChecks((prevState) => ({
      ...prevState,
      dataChanged: true,
      // update validation for field by name
      [name]: validateFieldByKey(name, value, editUserData),
    }));

    // Additional validation for password and confirmPassword fields
    if (name === "password") {
      setValidityChecks((prevState) => ({
        ...prevState,
        confirmPassword: validateFieldByKey("confirmPassword", value, { password: editUserData.confirmPassword }),
      }));
    }

    // Asynchronous validation for username
    if (name === "username") {
      if (value && value.trim().length >= NAME_MIN_LENGTH && value.trim().length <= NAME_MAX_LENGTH) {
        if (value.trim() === userRow.username) {
          setValidityChecks((prevState) => ({
            ...prevState,
            isUsernameValid: true,
          }));
        } else {
          const isUsernameValid = await validateUserName(backend.fetch, value);
          setValidityChecks((prevState) => ({
            ...prevState,
            isUsernameValid: isUsernameValid,
          }));
        }
      }
    }
  };

  // check if our form is valid in its current state
  useEffect(() => {
    setIsValid(Object.values(validityChecks).every((value) => value === true));
  }, [validityChecks]);

  // Define custom field validation - default behavior == field required
  // Passing editUserData object allows us to force check changes before state reflects update
  function validateFieldByKey(name, value, editUserData) {
    switch (name) {
      case "password":
        return value && value.length >= PASSWORD_MIN_LENGTH;
      case "confirmPassword":
        return value && editUserData.password === value;
      case "roles":
        return validateRoles(value);
      case "name":
        return value && value.trim().length >= NAME_MIN_LENGTH && value.trim().length <= NAME_MAX_LENGTH;
      case "username":
        return value && value.trim().length >= NAME_MIN_LENGTH && value.trim().length <= NAME_MAX_LENGTH;
      default:
        return value && value.length > 0;
    }
  }

  function validateRoles(roles) {
    if (roles && roles.length === 1) {
      let roleNameValid = true;
      for (const role of roles) {
        // each role should have a value
        if (!role.role_name || role.role_name.trim() === "") {
          roleNameValid = false;
          break;
        }
      }
      return roleNameValid;
    }
  }

  const { mutate: doUserMutation, isPending: userIsPending } = useMutation({
    mutationFn: async ({ user, method }) => {
      if (
        actionType.action === UserActions.EDIT_PROFILE_NAME.action ||
        actionType.action === UserActions.EDIT_PROFILE_PASSWORD.action
      ) {
        await updateMyUser(backend.fetch, user, method);
      } else if (UserActions.ADD_USER.action === actionType.action) {
        await createUser(backend.fetch, user);
      } else {
        await updateUser(backend.fetch, user, method);
      }
    },
    onSuccess: async () => {
      await refreshData();
      Snackbar.success("User updated");
      onClose();
    },
    onError: (err, data, context) => {
      Snackbar.error("User update failed: " + err);
    },
  });

  async function refreshData() {
    // for profile data changes we refresh the signed in users token
    switch (actionType.action) {
      case UserActions.EDIT_PROFILE_NAME.action:
        await refresh(login, setLogin);
        break;
      case UserActions.EDIT_PROFILE_PASSWORD.action:
        await refresh(login, setLogin);
        break;
      case UserActions.EDIT_USER.action:
        await queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.USER_OVERVIEW, backend.fetch] });
        break;
      case UserActions.ACTIVATE_DEACTIVATE_USER.action:
        await queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.USER_OVERVIEW, backend.fetch] });
        break;
      case UserActions.ADD_USER.action:
        await queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.USER_OVERVIEW, backend.fetch] });
        break;
      default:
        break;
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault();

    const user = {
      ...editUserData,
      ...(actionType.action === UserActions.ADD_USER.action || actionType.action === UserActions.EDIT_USER.action
        ? { company_name: selectedSearchAndSelectEntry || editUserData.company_name }
        : {}),
      user_id: userRow.user_id,
      action: actionType.action,
    };
    let method = "PUT";
    if (UserActions.ADD_USER.action === actionType.action) {
      method = "POST";
    }
    doUserMutation({ user, method });
  };

  const renderFields = () => {
    const usernameField = (
      <CustomTextField
        name="username"
        label="Username"
        autoComplete="username"
        fullWidth
        value={editUserData.username ?? ""}
        onChange={handleFieldValueEvent}
        error={validityChecks.isUsernameValid === false}
        helperText={validityChecks.isUsernameValid === false ? "Username already in use" : ""}
      />
    );

    const nameField = (
      <CustomTextField
        name="name"
        label="Name"
        autoComplete="name"
        fullWidth
        value={editUserData.name ?? ""}
        onChange={handleFieldValueEvent}
      />
    );

    const roleField = (
      <CustomRoleSelectField
        name="roles"
        label="Role"
        required={true}
        values={editUserData.roles ?? []}
        onChange={handleFieldValueEvent}
        options={userRolePickListOptions}
        error={validityChecks.roles === false}
        helperText={validityChecks.roles === false ? "Invalid role" : ""}
      />
    );
    const companyField = (
      <SearchAndSelectTable
        searchPlaceholder={"search company"}
        searchTerm={searchAndSelectSearchTerm}
        handleSearchChange={handleSearchChange}
        columns={[{ accessorKey: companySearchAndSelectKey, header: "COMPANY NAME" }]}
        filteredData={filteredSearchAndSelectData}
        isLoading={false}
        onRowSelectionChange={onTableRowSelectionChange}
        colKey={companySearchAndSelectKey}
        selectedEntry={selectedSearchAndSelectEntry}
      />
    );

    const currentPasswordField = (
      <PasswordField
        name="currentPassword"
        label="Current Password"
        autoComplete="current-password"
        value={editUserData.currentPassword ?? ""}
        onChange={handleFieldValueEvent}
        showPassword={showPassword}
        toggleShowPassword={() => setShowPassword(!showPassword)}
      />
    );

    const passwordField = (
      <PasswordField
        name="password"
        label="Password"
        autoComplete="new-password"
        value={editUserData.password ?? ""}
        onChange={handleFieldValueEvent}
        showPassword={showPassword}
        toggleShowPassword={() => setShowPassword(!showPassword)}
        error={!validityChecks.password}
        helperText={
          !validityChecks.password ? "Password should contain at least " + PASSWORD_MIN_LENGTH + " characters" : ""
        }
      />
    );

    const confirmPasswordField = (
      <PasswordField
        name="confirmPassword"
        label="Confirm Password"
        autoComplete="new-password"
        value={editUserData.confirmPassword ?? ""}
        onChange={handleFieldValueEvent}
        showPassword={showConfirmPassword}
        toggleShowPassword={() => setShowConfirmPassword(!showConfirmPassword)}
        error={!validityChecks.confirmPassword}
        helperText={!validityChecks.confirmPassword ? "Passwords do not match" : ""}
      />
    );

    switch (actionType.action) {
      case UserActions.EDIT_PROFILE_NAME.action:
        return (
          <>
            {usernameField}
            {nameField}
          </>
        );
      case UserActions.EDIT_USER.action:
        return (
          <>
            {usernameField}
            {nameField}
            {roleField}
            {companyField}
          </>
        );
      case UserActions.RESET_USER_PASSWORD.action:
        return (
          <>
            {passwordField}
            {confirmPasswordField}
          </>
        );
      case UserActions.EDIT_PROFILE_PASSWORD.action:
        return (
          <>
            {currentPasswordField}
            {passwordField}
            {confirmPasswordField}
          </>
        );
      case UserActions.ADD_USER.action:
        return (
          <>
            {usernameField}
            {nameField}
            {roleField}
            {companyField}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Box p={2} component="form" onSubmit={handleSubmit}>
        <DialogContent>
          {open && !(companiesError || rolesError) ? renderFields() : <p>Error loading data.</p>}
        </DialogContent>
        <DialogActions>
          {!userIsPending ? (
            <Button type="submit" variant="contained" color="primary" disabled={!isValid}>
              Save Changes
            </Button>
          ) : (
            <CircularProgress size={24} />
          )}
        </DialogActions>
      </Box>
    </Popover>
  );
};

export default UserPopover;
